export const BottomMenuIds = {
	AlternativeProducts: "alternative-products",
	BackgroundColor: "backgrounds-color",
	BackgroundPattern: "backgrounds-pattern",
	Backgrounds: "backgrounds",
	DateSelection: "date-selection",
	Designs: "designs",
	FontFamilies: "font-families",
	Layouts: "layouts",
	Map: "map",
	None: undefined,
	Options: "options",
	Photo: "photo",
	PhotoMap: "photo-map",
	PhotoBrightness: "photo-brightness",
	PhotoContrast: "photo-contrast",
	PhotoEditing: "photo-editing",
	PhotoFilters: "photo-filters",
	PhotoRotate: "photo-rotate",
	PhotoSaturation: "photo-saturation",
	PhotoSelection: "photo-selection",
	PhotoTransform: "photo-transform",
	PhotoZoneAdd: "photo-zone-add",
	PhotoZoneClear: "photo-zone-clear",
	PhotoZoneDelete: "photo-zone-delete",
	PhotoZoom: "photo-zoom",
	ProductOptions: "product-options",
	Root: "",
	Symbols: "symbols",
	SymbolsList: "symbols-list",
	SymbolsZoneDelete: "symbols-zone-delete",
	Text: "text",
	TextAlignment: "text-alignment",
	TextColor: "text-color",
	TextSize: "text-size",
	TextStyle: "text-style",
	TextValue: "text-value",
	TextZoneAdd: "text-zone-add",
	TextZoneClear: "text-zone-clear",
	TextZoneDelete: "text-zone-delete",
	ZoneDepth: "zone-depth",
} as const;

/*
We are at an old Typescript version. In the neweer typescript version we can do this:
export type BottomMenuIdValues = keyof {
	[P in keyof typeof BottomMenuIds & string as `${typeof BottomMenuIds[P]}`]: typeof BottomMenuIds[P];
}
*/

export type ROOT_SUB_MENU_ID =
	| typeof BottomMenuIds.Backgrounds
	| typeof BottomMenuIds.Designs
	| typeof BottomMenuIds.Layouts
	| typeof BottomMenuIds.Options
	| typeof BottomMenuIds.Photo
	| typeof BottomMenuIds.Map
	| typeof BottomMenuIds.Symbols
	| typeof BottomMenuIds.Text;

export type PHOTO_SUB_MENU_ID =
	| typeof BottomMenuIds.PhotoEditing
	| typeof BottomMenuIds.PhotoFilters
	| typeof BottomMenuIds.PhotoRotate
	| typeof BottomMenuIds.PhotoSelection
	| typeof BottomMenuIds.PhotoZoneAdd
	| typeof BottomMenuIds.PhotoZoneClear
	| typeof BottomMenuIds.PhotoZoneDelete
	| typeof BottomMenuIds.ZoneDepth
	| typeof BottomMenuIds.PhotoMap;

export type PHOTO_EDITING_MENU_ID =
	| typeof BottomMenuIds.PhotoBrightness
	| typeof BottomMenuIds.PhotoContrast
	| typeof BottomMenuIds.PhotoFilters
	| typeof BottomMenuIds.PhotoSaturation;

export type PHOTO_TRANSFORM_MENU_ID = typeof BottomMenuIds.PhotoRotate;

export type TEXT_SUB_MENU_ID =
	| typeof BottomMenuIds.FontFamilies
	| typeof BottomMenuIds.TextAlignment
	| typeof BottomMenuIds.TextColor
	| typeof BottomMenuIds.TextSize
	| typeof BottomMenuIds.TextStyle
	| typeof BottomMenuIds.TextValue
	| typeof BottomMenuIds.TextZoneAdd
	| typeof BottomMenuIds.TextZoneClear
	| typeof BottomMenuIds.TextZoneDelete
	| typeof BottomMenuIds.ZoneDepth;

export type BACKGROUNDS_SUB_MENU_ID =
	| typeof BottomMenuIds.BackgroundColor
	| typeof BottomMenuIds.BackgroundPattern;

type OPTIONS_SUB_MENU_ID =
	| typeof BottomMenuIds.AlternativeProducts
	| typeof BottomMenuIds.DateSelection
	| typeof BottomMenuIds.ProductOptions;

export type SYMBOLS_SUB_MENU_ID =
	| typeof BottomMenuIds.SymbolsList
	| typeof BottomMenuIds.SymbolsZoneDelete
	| typeof BottomMenuIds.ZoneDepth;

// type PRODUCT_OPTIONS_SUB_MENU_ID = typeof BottomMenuIds.ProductOptions;

export const makePhotoMenu = <T extends PHOTO_SUB_MENU_ID>(subMenuId: T) =>
	`${BottomMenuIds.Photo}/${subMenuId}` as const;

export const makePhotoEditMenu = <T extends PHOTO_EDITING_MENU_ID>(subMenuId: T) =>
	`${BottomMenuIds.PhotoEditing}/${subMenuId}` as const;

export const makeTextMenu = <T extends TEXT_SUB_MENU_ID>(subMenu: T) =>
	`${BottomMenuIds.Text}/${subMenu}` as const;

export const makeBackgroundMenu = <T extends BACKGROUNDS_SUB_MENU_ID>(subMenu: T) =>
	`${BottomMenuIds.Backgrounds}/${subMenu}` as const;

const makeOptionsMenu = <T extends OPTIONS_SUB_MENU_ID>(subMenu: T) =>
	`${BottomMenuIds.Options}/${subMenu}` as const;

const makeSymbolsMenu = <T extends SYMBOLS_SUB_MENU_ID>(subMenu: T) =>
	`${BottomMenuIds.Symbols}/${subMenu}` as const;

export const BottomMenuPaths = {
	AlternativeProducts: makeOptionsMenu(BottomMenuIds.AlternativeProducts),
	BackgroundColor: makeBackgroundMenu(BottomMenuIds.BackgroundColor),
	BackgroundPattern: makeBackgroundMenu(BottomMenuIds.BackgroundPattern),
	Backgrounds: BottomMenuIds.Backgrounds,
	DateSelection: makeOptionsMenu(BottomMenuIds.DateSelection),
	Designs: `${BottomMenuIds.Root}/${BottomMenuIds.Designs}`,
	FontFamilies: makeTextMenu(BottomMenuIds.FontFamilies),
	Layouts: `${BottomMenuIds.Root}/${BottomMenuIds.Layouts}`,
	Options: BottomMenuIds.Options,
	Photo: BottomMenuIds.Photo,
	PhotoBrightness: makePhotoEditMenu(BottomMenuIds.PhotoBrightness),
	PhotoContrast: makePhotoEditMenu(BottomMenuIds.PhotoContrast),
	PhotoEditing: BottomMenuIds.PhotoEditing,
	PhotoFilters: makePhotoEditMenu(BottomMenuIds.PhotoFilters),
	PhotoSaturation: makePhotoEditMenu(BottomMenuIds.PhotoSaturation),
	PhotoSelection: makePhotoMenu(BottomMenuIds.PhotoSelection),
	PhotoTransform: BottomMenuIds.PhotoTransform,
	PhotoZoneDepth: makePhotoMenu(BottomMenuIds.ZoneDepth),
	ProductOptions: makeOptionsMenu(BottomMenuIds.ProductOptions),
	Root: BottomMenuIds.Root,
	Symbols: BottomMenuIds.Symbols,
	SymbolsList: makeSymbolsMenu(BottomMenuIds.SymbolsList),
	SymbolsZoneDepth: makeSymbolsMenu(BottomMenuIds.ZoneDepth),
	Text: BottomMenuIds.Text,
	TextAlignment: makeTextMenu(BottomMenuIds.TextAlignment),
	TextColor: makeTextMenu(BottomMenuIds.TextColor),
	TextSize: makeTextMenu(BottomMenuIds.TextSize),
	TextStyle: makeTextMenu(BottomMenuIds.TextStyle),
	TextValue: BottomMenuIds.TextValue,
	TextZoneDepth: makeTextMenu(BottomMenuIds.ZoneDepth),
} as const;
